import {
  type FunctionMetadata,
  loadingMessageFromFunctionCall,
} from '@github-ui/copilot-chat/components/FunctionLoadingUtils'
import type {CopilotChatReference, FunctionCalledStatus, SkillExecution} from '@github-ui/copilot-chat-types'
import {WithShimmerEffect} from '@github-ui/copilot-chat-visuals/WithShimmerEffect'
import {ChevronRightIcon} from '@primer/octicons-react'
import {useMemo} from 'react'

import styles from './ToolCallHeader.module.css'

interface ToolCallHeaderProps {
  isOpen: boolean
  handleClick: () => void
  waitingForCopilotContent: boolean
  currentSkillExecution: SkillExecution | undefined
  toolCallsMetadata: ToolLogEntry[]
  contentId?: string
}

export interface ToolLogEntry extends FunctionMetadata {
  logMessage: string
  status: FunctionCalledStatus
  references?: CopilotChatReference[]
}

const COMPLETED_MESSAGE = 'Copilot finished thinking'

export function ToolCallHeader({
  isOpen,
  handleClick,
  waitingForCopilotContent,
  currentSkillExecution,
  toolCallsMetadata,
  contentId = 'tool-call-header',
}: ToolCallHeaderProps) {
  const toolCallMessage = useMemo(
    () => (currentSkillExecution ? loadingMessageFromFunctionCall(currentSkillExecution) : 'Thinking...'),
    [currentSkillExecution],
  )

  // Track when all tools have finished
  const allToolsFinished = useMemo(
    () => toolCallsMetadata.length > 0 && toolCallsMetadata.every(tool => ['completed', 'error'].includes(tool.status)),
    [toolCallsMetadata],
  )
  const isComplete = !waitingForCopilotContent && allToolsFinished
  return (
    <div data-active={waitingForCopilotContent} data-expanded={isOpen} className={styles.container}>
      <div className={styles.mainContent}>
        <span className={!isComplete ? styles.textWithShimmer : styles.text} id="header-title">
          {!isComplete && <WithShimmerEffect className={styles.titleWithShimmer}>{toolCallMessage}</WithShimmerEffect>}
          {isComplete && <span>{COMPLETED_MESSAGE}</span>}
        </span>
        <button
          onClick={handleClick}
          type="button"
          className={styles.toggleButton}
          aria-expanded={isOpen}
          aria-controls={contentId}
          aria-labelledby="header-title"
        >
          <ChevronRightIcon className={styles.icon} data-expanded={isOpen} />
        </button>
      </div>
    </div>
  )
}
